import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import {
  LogOut, Newspaper, Home, Megaphone, FileEdit, Inbox, Mail, Phone, Building2,
  Settings as SettingsIcon,
  CheckCircle2, Circle, Trash2, RefreshCw,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { inquiriesAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';

const SUBJECT_LABEL = {
  reklam: 'Reklam',
  sponsorluk: 'Sponsorluk',
  isbirligi: 'İş Birliği',
  diger: 'Diğer',
};

const SUBJECT_COLOR = {
  reklam: 'bg-red-100 text-red-700',
  sponsorluk: 'bg-amber-100 text-amber-700',
  isbirligi: 'bg-blue-100 text-blue-700',
  diger: 'bg-gray-100 text-gray-700',
};

const InquiryRow = ({ item, onToggleRead, onDelete }) => {
  const subjectClass = SUBJECT_COLOR[item.subject] || 'bg-gray-100 text-gray-700';
  return (
    <div
      className={`bg-white border rounded-lg overflow-hidden transition-shadow hover:shadow-sm ${
        item.is_read ? 'border-gray-200' : 'border-red-200 ring-1 ring-red-100'
      }`}
      data-testid={`inquiry-row-${item.id}`}
    >
      <div className="p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={() => onToggleRead(item)}
              title={item.is_read ? 'Okunmadı olarak işaretle' : 'Okundu olarak işaretle'}
              className="flex-shrink-0"
              data-testid={`inquiry-toggle-read-${item.id}`}
            >
              {item.is_read ? (
                <CheckCircle2 className="w-5 h-5 text-green-500" />
              ) : (
                <Circle className="w-5 h-5 text-red-500 fill-red-500/30" />
              )}
            </button>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-bold text-gray-900 truncate">{item.full_name}</h3>
                {!item.is_read && (
                  <Badge className="bg-red-600 hover:bg-red-600 text-white text-[10px] px-1.5">YENİ</Badge>
                )}
                <span className={`text-[11px] font-semibold uppercase px-2 py-0.5 rounded ${subjectClass}`}>
                  {SUBJECT_LABEL[item.subject] || item.subject}
                </span>
              </div>
              {item.company && (
                <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                  <Building2 className="w-3.5 h-3.5" />{item.company}
                </p>
              )}
            </div>
          </div>
          <div className="text-xs text-gray-400 flex-shrink-0">
            {format(new Date(item.created_at), 'd MMM yyyy HH:mm', { locale: tr })}
          </div>
        </div>

        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
          <a
            href={`mailto:${item.email}`}
            className="flex items-center gap-2 text-gray-700 hover:text-red-600 break-all"
          >
            <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />{item.email}
          </a>
          {item.phone && (
            <a
              href={`tel:${item.phone.replace(/\s+/g, '')}`}
              className="flex items-center gap-2 text-gray-700 hover:text-red-600"
            >
              <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />{item.phone}
            </a>
          )}
        </div>

        <div className="mt-4 bg-gray-50 rounded-lg p-4 text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
          {item.message}
        </div>

        <div className="mt-4 flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => onToggleRead(item)}
            data-testid={`inquiry-read-btn-${item.id}`}
          >
            {item.is_read ? 'Okunmadı yap' : 'Okundu işaretle'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onDelete(item)}
            className="text-red-600 border-red-200 hover:bg-red-50"
            data-testid={`inquiry-delete-btn-${item.id}`}
          >
            <Trash2 className="w-4 h-4 mr-1" /> Sil
          </Button>
        </div>
      </div>
    </div>
  );
};

const AdminInquiries = () => {
  const { user, logout, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all' | 'unread'
  const [pendingDelete, setPendingDelete] = useState(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) navigate('/admin/giris');
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated) load();
  }, [isAuthenticated]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await inquiriesAPI.list();
      const data = res.data || {};
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch {
      toast.error('Talepler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const toggleRead = async (item) => {
    try {
      const res = await inquiriesAPI.update(item.id, { is_read: !item.is_read });
      const updated = res.data;
      setItems((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
      setUnread((prev) => prev + (updated.is_read ? -1 : 1));
    } catch {
      toast.error('Güncellenemedi');
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    try {
      await inquiriesAPI.delete(pendingDelete.id);
      setItems((prev) => prev.filter((i) => i.id !== pendingDelete.id));
      if (!pendingDelete.is_read) setUnread((p) => Math.max(0, p - 1));
      toast.success('Talep silindi');
    } catch {
      toast.error('Silinemedi');
    } finally {
      setPendingDelete(null);
    }
  };

  const visible = filter === 'unread' ? items.filter((i) => !i.is_read) : items;

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-gray-500">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-inquiries">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-[#0F172A] text-white p-4 z-50">
        <div className="mb-8">
          <h1 className="text-lg font-bold tracking-tight">HABER TÜRKİYE'DEN</h1>
          <p className="text-gray-400 text-xs mt-1">Yönetim Paneli</p>
        </div>

        <nav className="space-y-1">
          <Link to="/admin" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors">
            <Newspaper className="w-5 h-5" /><span>Haberler</span>
          </Link>
          <Link to="/admin/talepler" className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-white/10 text-white">
            <Inbox className="w-5 h-5" />
            <span className="font-medium flex-1">Gelen Talepler</span>
            {unread > 0 && (
              <Badge className="bg-red-600 hover:bg-red-600 text-white text-[10px] px-1.5">{unread}</Badge>
            )}
          </Link>
          <Link to="/admin/reklamlar" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors">
            <Megaphone className="w-5 h-5" /><span>Reklam Yönetimi</span>
          </Link>
          <Link to="/admin/ayarlar" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors">
            <SettingsIcon className="w-5 h-5" /><span>Site Ayarları</span>
          </Link>

          <div className="pt-4 pb-1">
            <p className="px-3 text-xs uppercase font-bold text-gray-500 tracking-wider">Sayfa Yönetimi</p>
          </div>
          <Link to="/admin/sayfa/hakkimizda" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors">
            <FileEdit className="w-5 h-5" /><span>Hakkımızda</span>
          </Link>
          <Link to="/admin/sayfa/reklam-isbirligi" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors">
            <FileEdit className="w-5 h-5" /><span>Reklam & İş Birliği</span>
          </Link>

          <div className="pt-4">
            <Link to="/" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors">
              <Home className="w-5 h-5" /><span>Siteyi Görüntüle</span>
            </Link>
          </div>
        </nav>

        <div className="absolute bottom-4 left-4 right-4">
          <div className="border-t border-gray-700 pt-4 mb-4">
            <p className="text-gray-400 text-sm">{user?.username}</p>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-gray-400 hover:text-white hover:bg-white/5"
            onClick={() => { logout(); navigate('/admin/giris'); }}
          >
            <LogOut className="w-4 h-4 mr-2" />Çıkış Yap
          </Button>
        </div>
      </aside>

      <main className="ml-64 p-8 max-w-5xl">
        <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Gelen Talepler</h2>
            <p className="text-gray-500 text-sm mt-1">
              Reklam ve İş Birliği formundan gelen talepler. Toplam: <strong>{items.length}</strong>
              {unread > 0 && <> · Okunmamış: <strong className="text-red-600">{unread}</strong></>}
            </p>
          </div>
          <div className="flex gap-2">
            <div className="inline-flex bg-white border border-gray-200 rounded-lg p-1">
              <button
                onClick={() => setFilter('all')}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${filter === 'all' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}`}
                data-testid="inquiry-filter-all"
              >
                Tümü ({items.length})
              </button>
              <button
                onClick={() => setFilter('unread')}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${filter === 'unread' ? 'bg-red-600 text-white' : 'text-gray-600 hover:text-gray-900'}`}
                data-testid="inquiry-filter-unread"
              >
                Okunmamış ({unread})
              </button>
            </div>
            <Button variant="outline" size="sm" onClick={load} data-testid="inquiry-refresh">
              <RefreshCw className="w-4 h-4 mr-2" />Yenile
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center text-gray-500 py-16">Yükleniyor…</div>
        ) : visible.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg py-16 text-center text-gray-500">
            <Inbox className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="font-semibold text-gray-700">{filter === 'unread' ? 'Okunmamış talep yok' : 'Henüz talep yok'}</p>
            <p className="text-sm">Yeni talepler bu ekranda görünecek.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {visible.map((item) => (
              <InquiryRow
                key={item.id}
                item={item}
                onToggleRead={toggleRead}
                onDelete={(it) => setPendingDelete(it)}
              />
            ))}
          </div>
        )}
      </main>

      <AlertDialog open={Boolean(pendingDelete)} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Talebi Sil</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete ? `"${pendingDelete.full_name}" adlı kişinin talebi silinsin mi? Bu işlem geri alınamaz.` : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminInquiries;
